import React, { useState, useEffect } from 'react';
import { Input } from '@heroui/input';
import { getContactListById, submitContactListForm, fetchOrganizerById } from '../apiEndpoints';
import { type FormQuestion } from './FormGenerator';
import { btnAccent, card, surface, label as labelCls } from '@/components/ui/design';
import { Select, SelectItem } from '@/components/ui/AppSelect';

const fieldWrap = 'rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey95 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/10 dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60';
const inputCls = { inputWrapper: `h-14 ${fieldWrap}`, input: 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted' };
const nativeField = 'w-full rounded-2xl bg-grey99 px-4 py-3 text-base text-grey10 ring-1 ring-black/[0.04] transition-shadow placeholder:text-grey60 focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-60 dark:bg-white/[0.06] dark:text-white dark:ring-white/10 dark:placeholder:text-nightMuted dark:focus:ring-nightAccent/60';
const checkboxCls = 'h-4 w-4 rounded border-grey80 text-primary focus:ring-primary/40 dark:border-white/20 dark:accent-nightAccent';
const skel = 'rounded-full bg-grey95 dark:bg-white/10';

interface ContactListFormDisplayProps {
    listId: string;
}

interface ContactList {
    _id: string;
    name: string;
    description?: string;
    formTitle?: string;
    formDescription?: string;
    formQuestions?: FormQuestion[];
    isFormActive?: boolean;
    organizationId?: string;
}

interface Organization {
    _id: string;
    orgName: string;
    attachments?: {
        avatar?: {
            link: string;
        };
        banner?: {
            link: string;
        };
    };
}

export default function ContactListFormDisplay({ listId }: ContactListFormDisplayProps) {
    const [contactList, setContactList] = useState<ContactList | null>(null);
    const [organization, setOrganization] = useState<Organization | null>(null);
    const [answers, setAnswers] = useState<{ [questionId: string]: string | string[] }>({});
    const [errors, setErrors] = useState<{ [questionId: string]: string }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    // Contact information fields
    const [submitterName, setSubmitterName] = useState('');
    const [submitterEmail, setSubmitterEmail] = useState('');
    const [submitterPhone, setSubmitterPhone] = useState('');

    // Fetch contact list data from API
    useEffect(() => {
        const loadContactList = async () => {
            try {
                console.log('Loading contact list with ID:', listId);

                if (!listId || listId.trim() === '' || listId === 'undefined') {
                    throw new Error('Invalid contact list ID provided');
                }

                const list = await getContactListById(listId);
                console.log('Contact list loaded successfully:', list);

                if (!list || typeof list !== 'object') {
                    throw new Error('Invalid contact list data received from server');
                }

                if (!list.name) {
                    throw new Error('Contact list data is missing required fields');
                }

                if (!list.formQuestions || list.formQuestions.length === 0) {
                    throw new Error('This contact list does not have a form configured');
                }

                if (!list.isFormActive) {
                    throw new Error('This form is no longer accepting submissions');
                }

                setContactList(list);
                setLoadError(null);

                // Fetch organization info if organizationId exists
                if (list.organizationId) {
                    try {
                        const org = await fetchOrganizerById(list.organizationId);
                        setOrganization(org);
                    } catch (orgError) {
                        console.error('Error loading organization:', orgError);
                        // Don't fail the form if organization fetch fails
                    }
                }
            } catch (error) {
                console.error('Error loading contact list:', error);
                setContactList(null);

                let errorMessage = 'Failed to load form';
                if (error instanceof Error) {
                    errorMessage = error.message;
                } else if (typeof error === 'object' && error !== null) {
                    const apiError = error as any;
                    if (apiError.response?.data?.message) {
                        errorMessage = apiError.response.data.message;
                    } else if (apiError.response?.status) {
                        errorMessage = `Server error: ${apiError.response.status}`;
                    } else if (apiError.message) {
                        errorMessage = apiError.message;
                    }
                }

                setLoadError(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        loadContactList();
    }, [listId]);

    const handleAnswerChange = (questionId: string, value: string | string[]) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: value
        }));

        // Clear error when user starts typing
        if (errors[questionId]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[questionId];
                return newErrors;
            });
        }
    };

    const validateForm = (): boolean => {
        if (!contactList) return false;

        const newErrors: { [questionId: string]: string } = {};

        // Validate required contact fields
        if (!submitterName.trim()) {
            newErrors.submitterName = 'Name is required';
        }
        if (!submitterEmail.trim()) {
            newErrors.submitterEmail = 'Email is required';
        } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(submitterEmail)) {
            newErrors.submitterEmail = 'Invalid email format';
        }

        // Validate form questions - only check required questions
        contactList.formQuestions?.forEach(question => {
            if (question.required) {
                const answer = answers[question.id];
                if (!answer || (Array.isArray(answer) && answer.length === 0) || (typeof answer === 'string' && answer.trim() === '')) {
                    newErrors[question.id] = 'This field is required';
                }
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!contactList) return;

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            await submitContactListForm(listId, answers, submitterName, submitterEmail, submitterPhone);
            setIsSubmitted(true);
        } catch (error) {
            console.error('Error submitting form:', error);
            alert('There was an error submitting the form. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderQuestion = (question: FormQuestion) => {
        const answer = answers[question.id];
        const error = errors[question.id];

        return (
            <div key={question.id} className="space-y-2">
                <div className="flex items-center gap-2">
                    <label className="text-sm font-semibold text-grey10 dark:text-white">
                        {question.title}
                        {question.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                </div>

                {question.description && (
                    <p className="text-xs text-grey40 dark:text-nightMuted">{question.description}</p>
                )}

                {question.type === 'short-answer' && (
                    <Input
                        placeholder="Your answer"
                        value={(answer as string) || ''}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        disabled={isSubmitting}
                        className="w-full"
                        classNames={inputCls}
                    />
                )}

                {question.type === 'long-answer' && (
                    <textarea
                        placeholder="Your answer"
                        value={(answer as string) || ''}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        disabled={isSubmitting}
                        className={`${nativeField} resize-none`}
                        rows={4}
                    />
                )}

                {question.type === 'select' && (
                    <Select aria-label="Select" selectedKeys={[String(((answer as string) || '') ?? '')]} onChange={(e) => handleAnswerChange(question.id, e.target.value)} isDisabled={isSubmitting}>
                        <SelectItem key="">Select an option</SelectItem>
                        {question.options?.map((option) => (                            <SelectItem key={String(option)}>{option}</SelectItem>
                        ))}
                    </Select>
                )}

                {question.type === 'checkbox' && (
                    <div className="space-y-2">
                        {question.options?.map((option, index) => {
                            const selectedOptions = (answer as string[]) || [];
                            const isChecked = selectedOptions.includes(option);

                            return (
                                <div key={index} className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id={`${question.id}_${index}`}
                                        checked={isChecked}
                                        onChange={(e) => {
                                            const currentOptions = (answer as string[]) || [];
                                            if (e.target.checked) {
                                                handleAnswerChange(question.id, [...currentOptions, option]);
                                            } else {
                                                handleAnswerChange(question.id, currentOptions.filter(opt => opt !== option));
                                            }
                                        }}
                                        disabled={isSubmitting}
                                        className={checkboxCls}
                                    />
                                    <label htmlFor={`${question.id}_${index}`} className="text-sm text-grey20 dark:text-nightTextSecondary">
                                        {option}
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                )}

                {error && (
                    <div className="text-sm font-medium text-red-500 dark:text-red-400">{error}</div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="mx-auto max-w-2xl px-4 py-6 sm:p-6">
                <div className={`${card} animate-pulse space-y-6 p-6 sm:p-8`}>
                    <div className={`h-10 w-3/4 ${skel}`}></div>
                    <div className={`h-4 w-1/2 ${skel}`}></div>
                    <div className="space-y-4">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="space-y-2">
                                <div className={`h-4 w-1/3 ${skel}`}></div>
                                <div className="h-12 rounded-2xl bg-grey95 dark:bg-white/10"></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (!contactList && !loading) {
        return (
            <div className="mx-auto max-w-2xl px-4 py-6 text-center sm:p-6">
                <div className="mb-4 text-6xl">📝</div>
                <h2 className="mb-3 text-[clamp(28px,4vw,40px)] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">
                    {loadError ? 'Error Loading Form' : 'Form Not Found'}
                </h2>
                <p className="mb-6 text-base text-grey40 dark:text-nightMuted">
                    {loadError || 'The form you\'re looking for doesn\'t exist or has been removed.'}
                </p>
                {loadError && (
                    <div className="rounded-2xl bg-red-50 p-5 text-left ring-1 ring-red-200/60 dark:bg-red-500/10 dark:ring-red-500/20">
                        <h3 className="text-red-800 dark:text-red-400 font-medium mb-2">Technical Details:</h3>
                        <p className="text-red-700 dark:text-red-300 text-sm font-mono">{loadError}</p>
                        <p className="text-red-600 dark:text-red-400 text-sm mt-2">Contact List ID: {listId}</p>
                    </div>
                )}
            </div>
        );
    }

    if (isSubmitted) {
        return (
            <div className="mx-auto max-w-2xl px-4 py-6 sm:p-6">
                <div className={`${card} p-8 text-center sm:p-12`}>
                <div className="mb-4 text-6xl">✅</div>
                <h2 className="mb-3 text-[clamp(28px,4vw,40px)] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">Thank You!</h2>
                <p className="text-base text-grey40 dark:text-nightMuted">Your information has been submitted successfully.</p>
                {/* <div className="bg-green-50 border border-green-200 rounded p-4">
                    <p className="text-sm text-green-800">
                        You've been added to the <strong>{contactList?.name}</strong> contact list. We'll be in touch!
                    </p>
                </div> */}
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-2xl px-4 py-6 sm:p-6">
            <form onSubmit={handleSubmit} className={`${card} space-y-6 p-5 sm:p-8`}>
                {/* Form Header */}
                <div className="border-b border-black/[0.06] pb-6 text-center dark:border-white/10">
                    {/* Organization Logo and Name */}
                    {organization && (
                        <div className="mb-6 flex flex-row items-center gap-3">
                            {organization?.attachments?.avatar && (
                                <img
                                    src={organization?.attachments?.avatar?.link}
                                    alt={organization?.orgName}
                                    className="h-14 w-14 rounded-full object-cover ring-1 ring-black/[0.06] dark:ring-white/10"
                                />
                            )}
                            <h2 className="text-sm font-bold uppercase tracking-[0.18em] text-grey40 dark:text-nightMuted">
                                {organization?.orgName}
                            </h2>
                        </div>
                    )}
                    <h1 className="mb-3 text-[clamp(32px,5vw,48px)] font-bold leading-[0.95] tracking-[-0.05em] text-grey10 dark:text-white">
                        {contactList?.formTitle || contactList?.name}
                    </h1>
                    {contactList?.formDescription && (
                        <p className="mb-3 text-base leading-relaxed text-grey40 dark:text-nightMuted">{contactList.formDescription}</p>
                    )}
                    <p className="text-sm text-grey50 dark:text-nightMuted">
                        <span className="text-red-500">*</span> Required fields
                    </p>
                </div>

                {/* Contact Information */}
                <div className={`${surface} p-5`}>
                    <h3 className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-primary dark:text-nightAccent">Your Contact Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className={labelCls}>
                                Your Name *
                            </label>
                            <Input
                                placeholder="Enter your full name"
                                value={submitterName}
                                onChange={(e) => setSubmitterName(e.target.value)}
                                disabled={isSubmitting}
                                className="w-full"
                                classNames={inputCls}
                            />
                            {errors.submitterName && (
                                <div className="mt-1.5 text-sm font-medium text-red-500 dark:text-red-400">{errors.submitterName}</div>
                            )}
                        </div>
                        <div>
                            <label className={labelCls}>
                                Your Email *
                            </label>
                            <Input
                                type="email"
                                placeholder="Enter your email"
                                value={submitterEmail}
                                onChange={(e) => setSubmitterEmail(e.target.value)}
                                disabled={isSubmitting}
                                className="w-full"
                                classNames={inputCls}
                            />
                            {errors.submitterEmail && (
                                <div className="mt-1.5 text-sm font-medium text-red-500 dark:text-red-400">{errors.submitterEmail}</div>
                            )}
                        </div>
                        <div className="md:col-span-2">
                            <label className={labelCls}>
                                Your Phone (Optional)
                            </label>
                            <Input
                                type="tel"
                                placeholder="Enter your phone number"
                                value={submitterPhone}
                                onChange={(e) => setSubmitterPhone(e.target.value)}
                                disabled={isSubmitting}
                                className="w-full"
                                classNames={inputCls}
                            />
                        </div>
                    </div>
                </div>

                {/* Form Questions */}
                {contactList?.formQuestions && contactList.formQuestions.length > 0 && (
                    <div className="space-y-6">
                        <h3 className="text-xl font-bold tracking-[-0.02em] text-grey10 dark:text-white">Additional Information</h3>
                        {contactList.formQuestions.map((question, index) => (
                            <div key={question.id} className={`${surface} p-5`}>
                                <div className="flex items-start gap-3">
                                    <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-grey10 text-xs font-bold text-white dark:bg-white dark:text-nightBg">
                                        {index + 1}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        {renderQuestion(question)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Submit Button */}
                <div className="flex justify-center border-t border-black/[0.06] pt-6 dark:border-white/10">
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`${btnAccent} w-full px-8 disabled:cursor-not-allowed sm:w-auto`}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Information'}
                    </button>
                </div>
            </form>
        </div>
    );
}
